import React, { useState, useEffect } from "react";
import Header from "../../components/Header";
import Button from "../../components/Button";
import PageWrapper from "../../components/PageWrapper";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Switch,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { ReactComponent as EditIcon } from "../../assets/icons/paint.svg";
import { ReactComponent as MailIcon } from "../../assets/icons/mail.svg";
import UserModal from "../../components/UserModal";
import toast from "react-hot-toast";
import { getAllUsers, createUser as createUserAPI, updateUser as updateUserAPI, updateUserStatus as updateUserStatusAPI, deleteUser as deleteUserAPI, resendInviteEmail } from "../../services/userManagementApi";
import { ReactComponent as DeleteIcon } from "../../assets/icons/delete.svg";
import ConfirmationDialog from "../../components/ConfirmationDialog";

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedUser, setSelectedUser] = useState(null);

  // Loading states for individual actions
  const [actionLoading, setActionLoading] = useState({});

  // Generic dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await getAllUsers();
      setUsers(response);
    } catch (error) {
      toast.error("Failed to fetch users");
    }
    setLoading(false);
  };

  const handleCreateUser = async (userData) => {
    try {
      await createUserAPI(userData);
      toast.success("User created successfully! Temporary password sent.");
      await fetchUsers();
      setModalOpen(false);
    } catch (error) {
      toast.error("Failed to create user");
      throw error; // Re-throw to let formik handle the submission state
    }
  };

  const handleEditUser = async (userData) => {
    try {
      await updateUserAPI(selectedUser.id, userData);
      toast.success("User updated successfully");
      await fetchUsers();
      setModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error("Failed to update user");
      throw error; // Re-throw to let formik handle the submission state
    }
  };

  const handleModalSubmit = (userData) => {
    if (modalMode === "edit") {
      return handleEditUser(userData);
    }
    return handleCreateUser(userData);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedUser(null);
  };

  const openCreateModal = () => {
    setSelectedUser(null);
    setModalMode("create");
    setModalOpen(true);
  };

  const openDeleteDialog = (user) => {
    setConfirmDialog({
      open: true,
      title: "Delete User",
      message: `Are you sure you want to delete ${user.email}? This action cannot be undone.`,
      onConfirm: () => handleDeleteUser(user.id),
    });
  };

  const handleDeleteUser = async (userId) => {
    setActionLoading((prev) => ({ ...prev, [`delete-${userId}`]: true }));
    try {
      await deleteUserAPI(userId);
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    } finally {
      setActionLoading((prev) => ({ ...prev, [`delete-${userId}`]: false }));
      setConfirmDialog((prev) => ({ ...prev, open: false }));
    }
  };

  // Activate / Deactivate with confirmation dialog
  const handleToggleStatus = (userId, currentStatus) => {
    if (currentStatus === "active") {
      // deactivate → requires confirmation
      setConfirmDialog({
        open: true,
        title: "Deactivate User",
        message:
          "Are you sure you want to deactivate this user? They will not be able to log in while deactivated.",
        onConfirm: async () => {
          setActionLoading((prev) => ({ ...prev, [`status-${userId}`]: true }));
          try {
            await updateUserStatusAPI(userId, "inactive");
            toast.success("User deactivated successfully");
            fetchUsers();
          } catch {
            toast.error("Failed to deactivate user");
          } finally {
            setActionLoading((prev) => ({ ...prev, [`status-${userId}`]: false }));
            setConfirmDialog((prev) => ({ ...prev, open: false }));
          }
        },
      });
    } else {
      // activate → no dialog needed
      setActionLoading((prev) => ({ ...prev, [`status-${userId}`]: true }));
      updateUserStatusAPI(userId, "active")
        .then(() => {
          toast.success("User activated successfully");
          fetchUsers();
        })
        .catch(() => toast.error("Failed to activate user"))
        .finally(() => {
          setActionLoading((prev) => ({ ...prev, [`status-${userId}`]: false }));
        });
    }
  };

  const handleResendInvite = async (id, email) => {
    setActionLoading((prev) => ({ ...prev, [`resend-${id}`]: true }));
    try {
      await resendInviteEmail(id);
      toast.success(`Invite email resent to ${email}`);
    } catch {
      toast.error("Failed to resend invite email");
    } finally {
      setActionLoading((prev) => ({ ...prev, [`resend-${id}`]: false }));
    }
  };

  return (
    <PageWrapper>
      <div className="w-full h-full flex flex-col gap-6">
        {/** HEADER */}
        <Header text="User Management" />

        {/** ADD NEW USER BUTTON */}
        <div className="flex justify-between items-center">
          <div className="text-primary/70">
            Manage users and their permissions
          </div>
          <div className="shrink-0">
            <Button onClick={openCreateModal}>
              + Add New User
            </Button>
          </div>
        </div>

         {/* USERS TABLE */}
        <TableContainer component={Paper} className="shadow-md">
          <Table>
            <TableHead>
              <TableRow className="bg-gray-50">
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Invite Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.firstName} {user.lastName}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role.toUpperCase()}
                        color={user.role === "admin" ? "primary" : "default"}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={
                          user.userStatus === "CONFIRMED" ? "Accepted" : "Pending"
                        }
                        color={
                          user.userStatus === "CONFIRMED"
                            ? "success"
                            : "warning"
                        }
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </TableCell>

                    <TableCell>
                      <div className="flex items-center gap-2">
                        {actionLoading[`status-${user.id}`] ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Switch
                            checked={user.status === "active"}
                            onChange={() =>
                              handleToggleStatus(user.id, user.status)
                            }
                            size="small"
                            color="primary"
                            disabled={actionLoading[`status-${user.id}`]}
                          />
                        )}
                        <span className="text-sm">
                          {user.status === "active" ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </TableCell>

                    <TableCell align="left">
                      <div className="grid grid-cols-3 gap-2">
                        <Tooltip title="Edit User">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedUser(user);
                              setModalMode("edit");
                              setModalOpen(true);
                            }}
                          >
                            <EditIcon className="w-5 h-5" />
                          </IconButton>
                        </Tooltip>

                        {user.role !== "admin" && (
                          <Tooltip title="Delete User">
                            <IconButton
                              size="small"
                              onClick={() => openDeleteDialog(user)}
                              disabled={actionLoading[`delete-${user.id}`]}
                            >
                              {actionLoading[`delete-${user.id}`] ? (
                                <CircularProgress size={20} />
                              ) : (
                                <DeleteIcon className="w-5 h-5 text-red-600" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}

                        {user.userStatus === "FORCE_CHANGE_PASSWORD" && (
                          <Tooltip title="Resend Invite Email">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleResendInvite(user.id, user.email)
                              }
                              disabled={actionLoading[`resend-${user.id}`]}
                            >
                              {actionLoading[`resend-${user.id}`] ? (
                                <CircularProgress size={20} />
                              ) : (
                                <MailIcon className="w-5 h-5 text-green-600" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* CREATE USER MODAL */}
        <UserModal
          open={modalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          mode={modalMode}
          user={modalMode === "edit" ? selectedUser : null}
        />

        {/* GENERIC CONFIRMATION DIALOG */}
        <ConfirmationDialog
          open={confirmDialog.open}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onClose={() =>
            setConfirmDialog((prev) => ({ ...prev, open: false }))
          }
        />
      </div>
    </PageWrapper>
  );
};

export default UserManagementPage;
